import { Moment } from 'moment-timezone';
import moment from 'moment-timezone';
// eslint-disable-next-line import/no-extraneous-dependencies
import { getSchedule as getScheduleCron, stringToArray } from 'cron-converter';

import { getUserTimeZoneDate } from '@src/packages/utils';
import { DateUtil } from '@src/packages/utils/dates';
import { normalizeServerCronForCronConverter } from '@src/packages/utils/functions/schedule';

import TMembers from '@src/models/interfaces/members.interface';
import type { ScheduleFormValues } from '@src/models/interfaces/schedule.interface';
import TSchedule from '@src/models/interfaces/schedule.interface';
import TRepetition from '@src/models/interfaces/repetition.interface';

import { FAR_FUTURE_YEARS } from './constants';

// A schedule loaded from Firestore keeps its dates as Firestore Timestamps.
export type CurrentSchedule = TSchedule;

/** Empty members container used as the default form value. */
export const getDefaultMembers = (): TMembers => ({
  groups: [],
  users: [],
});

/** Normalize Firestore Timestamp or plain `{ seconds | _seconds }` shapes to ms. */
export const getScheduleTimestampMillis = (value: unknown): number | null =>
  DateUtil.getTimestampMs(value) ?? DateUtil.toDateOrNull(value)?.getTime() ?? null;

/**
 * Resolve the schedule start date taking the clone flow into account.
 * When cloning we always start "now"; otherwise we read the persisted start
 * and re-apply its timezone so the displayed date matches what was selected.
 */
export const getScheduleStartAt = (sch: CurrentSchedule, isToClone: boolean): Moment => {
  if (isToClone) {
    return moment();
  }

  const timeZone = sch.schedule?.timeZone;
  const startMs = getScheduleTimestampMillis(sch.schedule?.startAt);
  if (startMs === null) {
    return timeZone ? moment().tz(timeZone) : moment();
  }

  return timeZone ? moment(startMs).tz(timeZone) : moment(startMs);
};

interface GetEndAtParams {
  startAt: Moment;
  isRecurrent: boolean;
  endDate?: Moment | null;
  duration: number;
  isEndDateEnabled: boolean;
  repetition?: TRepetition;
}

/**
 * Mirrors RecurrenceInput option shapes: recurring patterns leave year/month/day/weekDay
 * empty in specific combinations; one-time ("none") keeps year/month/day filled.
 */
export const isScheduleRecurrent = (repetition?: TRepetition | null): boolean => {
  if (!repetition) {
    return false;
  }

  const hasWeekDay = (repetition.weekDay?.length ?? 0) > 0;
  const hasDay = (repetition.day?.length ?? 0) > 0;
  const hasMonth = (repetition.month?.length ?? 0) > 0;
  const hasYear = (repetition.year?.length ?? 0) > 0;

  const isDaily = !hasDay && !hasMonth && !hasYear && !hasWeekDay;
  const isWeekly = hasWeekDay && !hasDay && !hasMonth && !hasYear;
  const isMonthly = hasDay && !hasMonth && !hasYear && !hasWeekDay;
  const isYearly = hasDay && hasMonth && !hasYear && !hasWeekDay;

  return isDaily || isWeekly || isMonthly || isYearly;
};

export const getScheduleEndDateMoment = (sch: CurrentSchedule): Moment => {
  const endMs = getScheduleTimestampMillis(sch.schedule.endAt);
  const timeZone = sch.schedule.timeZone;

  if (endMs === null) {
    return moment().tz(timeZone).add(1, 'day').startOf('day');
  }

  return moment(endMs).tz(timeZone);
};

export const inferEndDateEnabled = (startMs: number | null, endMs: number | null): boolean => {
  if (startMs === null || endMs === null) {
    return false;
  }

  const farFutureMs = moment(startMs).add(FAR_FUTURE_YEARS, 'years').valueOf();
  const oneYearMs = 365 * 24 * 60 * 60 * 1000;

  return endMs < farFutureMs - oneYearMs;
};

export const haveSameWallClock = (left: Moment, right: Moment): boolean =>
  left.year() === right.year() &&
  left.month() === right.month() &&
  left.date() === right.date() &&
  left.hour() === right.hour() &&
  left.minute() === right.minute();

export const buildStartAtFromFormValues = (
  values: Pick<ScheduleFormValues, 'startDate' | 'startTime' | 'timeZone'>,
  fallback: Moment,
): Moment => {
  const { startDate, startTime, timeZone } = values;

  if (!startDate || startTime === undefined || startTime === null) {
    return fallback;
  }

  return moment(startDate)
    .clone()
    .startOf('day')
    .add(startTime, 'minutes')
    .tz(timeZone, true)
    .seconds(0);
};

export const resolveScheduleEndAt = ({
  startAt,
  isRecurrent,
  endDate,
  duration,
  isEndDateEnabled,
  repetition,
  existingSchedule,
  isUpdate,
}: GetEndAtParams & {
  existingSchedule?: CurrentSchedule | null;
  isUpdate?: boolean;
}): Moment => {
  const timeZone = existingSchedule?.schedule?.timeZone ?? startAt.tz() ?? moment.tz.guess();
  let endAt = getEndAt({
    startAt,
    isRecurrent,
    endDate,
    duration,
    isEndDateEnabled,
    repetition,
  });

  if (!isUpdate || !existingSchedule?.schedule) {
    return endAt;
  }

  const existingStartMs = getScheduleTimestampMillis(existingSchedule.schedule.startAt);
  const existingEndMs = getScheduleTimestampMillis(existingSchedule.schedule.endAt);

  if (existingStartMs === null || existingEndMs === null) {
    return endAt;
  }

  const durationMs = duration * 60 * 1000;
  const computedSpan = endAt.valueOf() - startAt.valueOf();
  const existingSpan = existingEndMs - existingStartMs;
  const durationChanged = existingSchedule.duration !== duration;

  // Avoid shrinking a wide schedule window to start + duration on incidental re-save.
  if (
    !durationChanged &&
    existingSpan > durationMs * 2 &&
    computedSpan <= durationMs * 2
  ) {
    endAt = moment(existingEndMs).tz(timeZone);
  }

  return endAt;
};

/**
 * Compute the schedule `endAt`.
 * - One-time executions (no end date and not recurrent) end at startAt + duration.
 * - Recurrent schedules with the end date disabled run "forever" (+50 years).
 * - Otherwise the end date is normalized to the start of the day, never before
 *   the day after startAt.
 */
export const getEndAt = ({
  startAt,
  isRecurrent,
  endDate,
  duration,
  isEndDateEnabled,
  repetition,
}: GetEndAtParams): Moment => {
  const nextDay = startAt.clone().add(1, 'day').startOf('day');
  const recurrent = isRecurrent || isScheduleRecurrent(repetition);

  // If the schedule does not repeat then "endDate" is undefined.
  const isOneTimeExecution = !endDate && !recurrent;
  if (isOneTimeExecution) {
    // If the schedule does not repeat then the schedule "endAt" should be the (startAt + duration)
    return startAt.clone().add(duration, 'minutes');
  }

  // NOTE: If endDateEnabled is false, add 50 years to the startDate
  const finalEndDate = !isEndDateEnabled
    ? startAt.clone().add(FAR_FUTURE_YEARS, 'years')
    : (endDate as Moment);
  return nextDay.isAfter(finalEndDate) ? nextDay : finalEndDate.clone().startOf('day');
};

/**
 * Whether the recurrence picker should be disabled because the next computed
 * execution would happen after the schedule end date (out of range).
 */
export const computeDisableByOutOfRange = (currentSchedule: CurrentSchedule | null): boolean => {
  if (!currentSchedule?.jobs) return false;

  try {
    const {
      jobs: { start: futureStart },
      schedule: { endAt },
    } = currentSchedule;
    const futureStartParsed = normalizeServerCronForCronConverter(futureStart.cron);
    const arr = stringToArray(futureStartParsed);
    const schedule = getScheduleCron(arr);
    const nextExecution = schedule.next().toJSDate();
    const endDate = DateUtil.toDateOrNull(endAt);
    if (!endDate) {
      return false;
    }
    return moment(nextExecution).isAfter(moment(endDate));
  } catch {
    return false;
  }
};

interface StartTimeParams {
  currentSchedule: CurrentSchedule | null;
  isToClone: boolean;
  startAt: Moment;
}

/** Start time, expressed as minutes from midnight, in the schedule timezone. */
export const getStartTimeMinutes = ({
  currentSchedule,
  isToClone,
  startAt,
}: StartTimeParams): number => {
  if (!currentSchedule?.schedule || isToClone) {
    return startAt.local().hours() * 60 + startAt.local().minutes();
  }

  const dte = getScheduleStartAt(currentSchedule, isToClone);
  return dte.minutes() + dte.hours() * 60;
};

interface RecurrenceDateParams {
  currentSchedule: CurrentSchedule | null;
  isToClone: boolean;
  startAt: Moment;
  profileTimeZone?: string;
}

/** Initial date for the recurrence input, normalized to the schedule timezone. */
export const getRecurrenceInitialDate = ({
  currentSchedule,
  isToClone,
  startAt,
  profileTimeZone,
}: RecurrenceDateParams): Moment => {
  if (!currentSchedule || isToClone) {
    return startAt.local();
  }
  // NOTE: invert timezones to get the timeZone selected from FIREBASE
  const dateWithTimeZone = getUserTimeZoneDate(
    startAt.valueOf(),
    profileTimeZone,
    currentSchedule.schedule.timeZone,
  ).jsDate;
  // NOTE: I need to do that to get real timezone.
  return moment(new Date(dateWithTimeZone));
};

/**
 * Remove a member (user or group) by id and rebuild the members container,
 * keeping each entry in its right bucket (groups have a `users` field).
 */
export const removeMemberById = (members: TMembers, userId: string): TMembers => {
  const allMembers = [...members.groups, ...members.users];
  const filtered = allMembers.filter(member => member.id !== userId);

  return filtered.reduce<TMembers>(
    (acc, val) => {
      // eslint-disable-next-line no-prototype-builtins
      const key = val.hasOwnProperty('users') ? 'groups' : 'users';
      (acc[key] as unknown[]).push(val);
      return acc;
    },
    { users: [], groups: [] },
  );
};
